import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import {
    RotateCcw, RotateCw, FlipVertical2, Copy, Trash2, FilePlus2, FileUp, Download, Save, Search, Loader2, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { buildBinderPdf } from "../lib/binder";
import { downloadBytes } from "../lib/pdfExport";
import { useI18n } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

const uid = () => Math.random().toString(36).slice(2, 10);

function Thumb({ p, doc, width, onDims }) {
    const ref = useRef(null);
    useEffect(() => {
        let task, off = false;
        (async () => {
            const cv = ref.current;
            if (!cv) return;
            if (p.blank) {
                const w = width, h = p.landscape !== (p.rot % 180 !== 0) ? width * 0.707 : width * 1.414;
                cv.width = w; cv.height = h;
                const ctx = cv.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
                onDims?.(w > h);
                return;
            }
            if (!doc) return;
            const pg = await doc.getPage(p.idx + 1);
            if (off) return;
            const base = pg.getViewport({ scale: 1, rotation: ((pg.rotate || 0) + p.rot) % 360 });
            const vp = pg.getViewport({ scale: width / base.width, rotation: ((pg.rotate || 0) + p.rot) % 360 });
            cv.width = vp.width; cv.height = vp.height;
            onDims?.(vp.width > vp.height);
            task = pg.render({ canvasContext: cv.getContext("2d"), viewport: vp });
            task.promise.catch(() => {});
        })();
        return () => { off = true; task?.cancel(); };
    }, [p.src, p.idx, p.rot, p.blank, p.landscape, doc, width]); // eslint-disable-line react-hooks/exhaustive-deps
    return <canvas ref={ref} className="max-w-full bg-white shadow" />;
}

// Acrobat-like Binder: combine files, reorder/rotate/copy/delete pages, then save as the project PDF.
export function BinderDialog({ open, onOpenChange, projectId, pdfData, comments, unitOffsets, pageNames, onSaved }) {
    const { t } = useI18n();
    const [pages, setPages] = useState([]);
    const [docs, setDocs] = useState({});
    const sources = useRef({});
    const [sel, setSel] = useState([]);
    const [active, setActive] = useState(null);
    const [orient, setOrient] = useState({});
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [projects, setProjects] = useState([]);
    const [numStart, setNumStart] = useState(1);
    const dragRef = useRef(null);
    const fileRef = useRef(null);

    const loadSource = async (key, bytes) => {
        sources.current[key] = { bytes };
        const doc = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
        setDocs((d) => ({ ...d, [key]: doc }));
        return doc;
    };

    useEffect(() => {
        if (!open) return;
        setSel([]); setSearch(""); sources.current = {}; setDocs({});
        api.get("/projects").then(({ data }) => setProjects(data.filter((p) => p.id !== projectId && p.my_role !== "commenter"))).catch(() => {});
        if (!pdfData) { setPages([]); return; }
        loadSource("cur", pdfData.slice(0)).then((doc) => {
            const list = Array.from({ length: doc.numPages }, (_, i) => ({ id: uid(), src: "cur", idx: i, rot: 0, orig: i + 1, label: pageNames?.[i + 1] || "" }));
            setPages(list); setActive(list[0]?.id || null);
        });
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const insertAt = () => {
        const idxs = sel.map((id) => pages.findIndex((p) => p.id === id)).filter((i) => i >= 0);
        return idxs.length ? Math.max(...idxs) + 1 : pages.length;
    };
    const insertPages = (np) => setPages((ps) => { const at = insertAt(); return [...ps.slice(0, at), ...np, ...ps.slice(at)]; });

    const addFiles = async (files) => {
        setBusy(true);
        try {
            for (const f of files) {
                let bytes;
                if (f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")) bytes = new Uint8Array(await f.arrayBuffer());
                else {
                    const fd = new FormData();
                    fd.append("file", f, f.name);
                    const r = await api.post("/convert-to-pdf", fd, { responseType: "arraybuffer" });
                    bytes = new Uint8Array(r.data);
                }
                const key = uid();
                const doc = await loadSource(key, bytes);
                insertPages(Array.from({ length: doc.numPages }, (_, i) => ({ id: uid(), src: key, idx: i, rot: 0, label: "", file: f.name })));
            }
        } catch (e) {
            toast.error(t("bd_add_failed"));
        } finally { setBusy(false); }
    };

    const targets = sel.length ? sel : active ? [active] : [];
    const rotate = (d, ids = targets) => setPages((ps) => ps.map((p) => (ids.includes(p.id) ? { ...p, rot: (((p.rot + d) % 360) + 360) % 360 } : p)));
    const duplicate = () => setPages((ps) => ps.flatMap((p) => (targets.includes(p.id) ? [p, { ...p, id: uid(), orig: undefined }] : [p])));
    const askDelete = () => {
        const del = pages.filter((p) => targets.includes(p.id));
        const left = pages.filter((p) => !targets.includes(p.id));
        const lostPages = [...new Set(del.map((p) => p.orig).filter((o) => o && !left.some((x) => x.orig === o)))];
        const bubbles = Object.values(unitOffsets || {}).filter((u) => lostPages.includes(u.page)).length;
        const cms = (comments || []).filter((c) => lostPages.includes(c.page)).length;
        if (bubbles || cms) setConfirmDel({ ids: targets, bubbles, cms });
        else doDelete(targets);
    };
    const doDelete = (ids) => { setPages((ps) => ps.filter((p) => !ids.includes(p.id))); setSel([]); setConfirmDel(null); };

    const clickThumb = (e, p) => {
        setActive(p.id);
        if (e.shiftKey && sel.length) {
            const a = pages.findIndex((x) => x.id === sel[sel.length - 1]), b = pages.findIndex((x) => x.id === p.id);
            setSel(pages.slice(Math.min(a, b), Math.max(a, b) + 1).map((x) => x.id));
        } else if (e.ctrlKey || e.metaKey) setSel((s) => (s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id]));
        else setSel([p.id]);
    };
    const onDrop = (targetId) => {
        const dragged = dragRef.current;
        dragRef.current = null;
        if (!dragged || dragged === targetId) return;
        const ids = sel.includes(dragged) ? sel : [dragged];
        setPages((ps) => {
            const moving = ps.filter((p) => ids.includes(p.id));
            const rest = ps.filter((p) => !ids.includes(p.id));
            const at = targetId ? rest.findIndex((p) => p.id === targetId) : rest.length;
            return [...rest.slice(0, at < 0 ? rest.length : at), ...moving, ...rest.slice(at < 0 ? rest.length : at)];
        });
    };

    const build = (list) => buildBinderPdf(list, sources.current);
    const downloadSel = async () => {
        setBusy(true);
        try { downloadBytes(await build(pages.filter((p) => targets.includes(p.id))), t("bd_pages_file")); } finally { setBusy(false); }
    };
    const addToProject = async (pid) => {
        if (!pid || !targets.length) return;
        setBusy(true);
        try {
            const target = projects.find((p) => p.id === pid);
            let list = [];
            if (target?.pdf_path) {
                const r = await api.get(`/projects/${pid}/pdf`, { responseType: "arraybuffer" });
                const tb = new Uint8Array(r.data);
                sources.current.__target = { bytes: tb };
                const tdoc = await PDFDocCount(tb);
                list = Array.from({ length: tdoc }, (_, i) => ({ src: "__target", idx: i, rot: 0 }));
            }
            const bytes = await build([...list, ...pages.filter((p) => targets.includes(p.id))]);
            const fd = new FormData();
            fd.append("file", new File([bytes], target?.pdf_filename || `${target?.name || "binder"}.pdf`, { type: "application/pdf" }));
            await api.post(`/projects/${pid}/pdf`, fd);
            toast.success(t("bd_added_to", { p: target?.name, n: targets.length }));
        } catch { toast.error(t("bd_add_other_failed")); } finally { setBusy(false); }
    };
    const renumber = () => setPages((ps) => ps.map((p, i) => ({ ...p, label: String(Number(numStart) + i) })));
    const save = async () => {
        if (!pages.length) { toast.error(t("bd_empty")); return; }
        setBusy(true);
        try {
            const bytes = await build(pages);
            const mapping = {};
            const newNames = {};
            pages.forEach((p, i) => {
                if (p.orig && mapping[p.orig] === undefined) mapping[p.orig] = i + 1;
                if (p.label) newNames[i + 1] = p.label;
            });
            const total = docs.cur?.numPages || 0;
            const deleted = Array.from({ length: total }, (_, i) => i + 1).filter((o) => mapping[o] === undefined);
            await onSaved({ bytes, mapping, deleted, pageNames: newNames });
            onOpenChange(false);
        } catch { toast.error(t("bd_save_failed")); } finally { setBusy(false); }
    };

    const q = search.trim().toLowerCase();
    const shown = pages.map((p, i) => ({ p, n: i + 1 })).filter(({ p, n }) => !q || String(n) === q || (p.label || "").toLowerCase().includes(q) || (p.file || "").toLowerCase().includes(q));
    const activePage = pages.find((p) => p.id === active);
    const tb = "h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md border border-[#1e293b] bg-[#111827] text-xs text-slate-200 hover:bg-[#182232] disabled:opacity-40 transition-colors";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="binder-dialog" className="max-w-[96vw] w-[96vw] h-[92vh] p-0 flex flex-col bg-[#0b0f17] border-[#1e293b] text-slate-100 gap-0"
                onKeyDown={(e) => { if (e.target.tagName !== "INPUT" && e.key === "Delete" && targets.length) askDelete(); }}>
                <DialogHeader className="px-4 py-3 border-b border-[#1e293b]">
                    <DialogTitle className="font-mono">PDF Binder</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">{t("bd_desc")}</DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-[#1e293b]">
                    <button data-testid="binder-add-files" className={tb} onClick={() => fileRef.current?.click()} disabled={busy}><FileUp className="w-3.5 h-3.5" /> {t("bd_add_files")}</button>
                    <input ref={fileRef} data-testid="binder-file-input" type="file" multiple className="hidden"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.xlsm" onChange={(e) => { const f = [...e.target.files]; e.target.value = ""; addFiles(f); }} />
                    <button data-testid="binder-blank" className={tb} onClick={() => insertPages([{ id: uid(), blank: true, rot: 0, label: "" }])}><FilePlus2 className="w-3.5 h-3.5" /> {t("bd_blank")}</button>
                    <span className="w-px h-6 bg-[#1e293b] mx-1" />
                    <button data-testid="binder-rot-left" className={tb} disabled={!targets.length} onClick={() => rotate(-90)}><RotateCcw className="w-3.5 h-3.5" /> {t("bd_rot_left")}</button>
                    <button data-testid="binder-rot-right" className={tb} disabled={!targets.length} onClick={() => rotate(90)}><RotateCw className="w-3.5 h-3.5" /> {t("bd_rot_right")}</button>
                    <button data-testid="binder-rot-180" className={tb} disabled={!targets.length} onClick={() => rotate(180)}><FlipVertical2 className="w-3.5 h-3.5" /> 180°</button>
                    <button data-testid="binder-copy" className={tb} disabled={!targets.length} onClick={duplicate}><Copy className="w-3.5 h-3.5" /> {t("bd_copy")}</button>
                    <button data-testid="binder-delete" className={`${tb} hover:text-rose-300`} disabled={!targets.length} onClick={askDelete}><Trash2 className="w-3.5 h-3.5" /> {t("bd_delete")}</button>
                    <button data-testid="binder-download-sel" className={tb} disabled={!targets.length || busy} onClick={downloadSel}><Download className="w-3.5 h-3.5" /> {t("bd_extract")}</button>
                    <select data-testid="binder-add-to-project" className={`${tb} pr-1`} value="" disabled={!targets.length || busy || !projects.length}
                        onChange={(e) => addToProject(e.target.value)}>
                        <option value="">{t("bd_add_other")}</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <span className="w-px h-6 bg-[#1e293b] mx-1" />
                    <span className="text-xs text-slate-400">{t("bd_num_from")}</span>
                    <input data-testid="binder-num-start" type="number" value={numStart} onChange={(e) => setNumStart(e.target.value)}
                        className="w-14 h-8 bg-[#111827] border border-[#1e293b] rounded-md text-xs px-2" />
                    <button data-testid="binder-renumber" className={tb} onClick={renumber}>{t("bd_renumber")}</button>
                    <div className="relative ml-auto">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-500" />
                        <input data-testid="binder-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("bd_search_ph")}
                            className="h-8 w-48 pl-7 bg-[#111827] border border-[#1e293b] rounded-md text-xs" />
                    </div>
                    <button data-testid="binder-save" onClick={save} disabled={busy}
                        className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-xs font-medium disabled:opacity-50">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t("bd_save")}
                    </button>
                </div>
                <div className="flex-1 min-h-0 flex">
                    <div data-testid="binder-thumbs" className="w-[300px] shrink-0 overflow-auto border-r border-[#1e293b] p-3 grid grid-cols-2 gap-3 content-start"
                        onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(null)}>
                        {shown.map(({ p, n }) => {
                            const cms = p.orig ? (comments || []).filter((c) => c.page === p.orig).length : 0;
                            const on = sel.includes(p.id);
                            return (
                                <div key={p.id} data-testid={`binder-page-${n}`} draggable
                                    onDragStart={() => { dragRef.current = p.id; }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.stopPropagation(); onDrop(p.id); }}
                                    onClick={(e) => clickThumb(e, p)}
                                    className={`rounded-md p-1.5 cursor-pointer border transition-colors ${on ? "border-blue-500 bg-blue-600/15" : active === p.id ? "border-slate-500" : "border-transparent hover:border-[#334155]"}`}>
                                    <div className="flex justify-center"><Thumb p={p} doc={docs[p.src]} width={118} onDims={(l) => setOrient((o) => (o[p.id] === l ? o : { ...o, [p.id]: l }))} /></div>
                                    <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-slate-400">
                                        <span data-testid={`binder-page-num-${n}`} className="text-slate-100 font-semibold">{n}</span>
                                        <span data-testid={`binder-page-orient-${n}`}>{orient[p.id] ? "Landscape" : "Portrait"}{p.rot ? ` · ${p.rot}°` : ""}</span>
                                        <span className="flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" />{cms}</span>
                                    </div>
                                    <input data-testid={`binder-page-label-${n}`} value={p.label || ""} placeholder={p.file || (p.blank ? t("bd_blank_ph") : t("bd_name_ph"))}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setPages((ps) => ps.map((x) => (x.id === p.id ? { ...x, label: e.target.value } : x)))}
                                        className="mt-1 w-full bg-[#111827] border border-[#1e293b] rounded text-[10px] px-1.5 py-0.5" />
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex-1 min-w-0 overflow-auto p-6 flex flex-col items-center gap-3 bg-[#0f172a]">
                        {activePage ? (
                            <>
                                <div className="flex items-center gap-1.5">
                                    <button className={tb} onClick={() => rotate(-90, [activePage.id])}><RotateCcw className="w-3.5 h-3.5" /></button>
                                    <button className={tb} onClick={() => rotate(90, [activePage.id])}><RotateCw className="w-3.5 h-3.5" /></button>
                                    <button data-testid="binder-toggle-orient" className={tb} onClick={() => rotate(90, [activePage.id])}>
                                        {orient[activePage.id] ? "→ Portrait" : "→ Landscape"}
                                    </button>
                                </div>
                                <Thumb p={activePage} doc={docs[activePage.src]} width={orient[activePage.id] ? 900 : 620} />
                            </>
                        ) : <p className="text-slate-500 text-sm mt-20">{pdfData ? t("bd_pick_page") : t("bd_add_to_create")}</p>}
                    </div>
                </div>
                <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
                    <AlertDialogContent data-testid="binder-delete-confirm" className="bg-[#111827] border-[#1e293b] text-slate-100">
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t("bd_del_title")}</AlertDialogTitle>
                            <AlertDialogDescription className="text-slate-400">
                                {t("bd_del_desc", { b: confirmDel?.bubbles || 0, c: confirmDel?.cms || 0 })}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="binder-delete-cancel" className="bg-transparent border-[#1e293b] text-slate-200">{t("bd_cancel")}</AlertDialogCancel>
                            <AlertDialogAction data-testid="binder-delete-yes" onClick={() => doDelete(confirmDel.ids)} className="bg-rose-600 hover:bg-rose-500">{t("bd_del_yes")}</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}

async function PDFDocCount(bytes) {
    const doc = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
    const n = doc.numPages;
    doc.destroy();
    return n;
}
